import { useRef } from "react"

import { Checkbox, Divider, Dropdown, Flex, Space, Typography } from "antd"
import cls from "classname"
import { DndProvider, useDrag, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useTranslation } from "react-i18next"

import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import Icon from "../../Icon"
import {
  adminUsersColumnIconMap,
  alertsColumnIconMap,
  creditsColumnIconMap,
  perksAdminColumnIconMap,
  usersColumnIconMap
} from "../../Utils/utils"
import Button from "../Button"

import "./styles.less"

const { Text } = Typography

const iconsByType = {
  credits: creditsColumnIconMap,
  users: usersColumnIconMap,
  adminUsers: adminUsersColumnIconMap,
  perksAdmin: perksAdminColumnIconMap
}

const ManageColumn = ({
  myColumns,
  onChangeColumns,
  pinnedColumns,
  iconsType,
  iconMap: iconMapProp,
  disabled,
  dataTour
}) => {
  const { t } = useTranslation()
  const externalDemoMode = useExternalDemoMode()

  const iconMap = iconMapProp || iconsByType?.[iconsType] || alertsColumnIconMap

  // change order
  const moveRow = async (dragIndex, hoverIndex) => {
    const clonedList = [...myColumns]
    const el = clonedList.splice(dragIndex, 1)[0]
    clonedList.splice(hoverIndex, 0, el)
    onChangeColumns(clonedList)
  }

  const handleChangeColumn = (e, key) => {
    e.stopPropagation()
    const newColumns = [...myColumns].map((el) =>
      el.field_id === key ? { ...el, active: !el.active } : el
    )
    onChangeColumns(newColumns)
  }

  return (
    <Dropdown
      overlayClassName="custom-menu with-drag-drop"
      getPopupContainer={() => document.getElementById("main-layout")}
      overlayStyle={{ width: 268 }}
      menu={{
        items: [
          ...myColumns?.map((column, index) => ({
            key: column.field_id,
            label: (
              <DraggableItem
                index={index}
                label={column.label}
                moveRow={moveRow}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={column.active}
                  //disabled={disabled}
                  onChange={(e) => handleChangeColumn(e, column.field_id)}
                >
                  <Flex align={"center"}>
                    {iconMap[column.field_id] && (
                      <Icon
                        type={iconMap[column.field_id]}
                        style={{ fontSize: 20, lineHeight: 1 }}
                      />
                    )}
                    <Text>{column.label}</Text>
                  </Flex>
                </Checkbox>
              </DraggableItem>
            )
          }))
        ],
        onClick: ({ key, domEvent }) => handleChangeColumn(domEvent, key)
      }}
      trigger={["click"]}
      placement="bottomLeft"
      dropdownRender={(menu) => (
        <div className={"ant-dropdown-menu-wrapper"}>
          <Space size={4} direction="vertical" style={{ padding: "8px 8px 0" }}>
            <Text>{t("Pinned columns")}</Text>
            {pinnedColumns ? (
              pinnedColumns.map((el) => (
                <Text type="secondary" strong>
                  {el === "#" ? el : t(el)}
                </Text>
              ))
            ) : (
              <>
                <Text type="secondary" strong>
                  #
                </Text>
                <Text type="secondary" strong>
                  {t("Status")}
                </Text>
              </>
            )}
          </Space>
          <Divider style={{ margin: "16px 0" }} />
          <DndProvider backend={HTML5Backend}>{menu}</DndProvider>
        </div>
      )}
    >
      <div
        className="edit-columns-trigger"
        onClick={(e) => e.stopPropagation()}
        data-tour={dataTour}
      >
        <Button
          disabled={disabled || externalDemoMode}
          className="flex center"
          icon={<Icon type="settings" style={{ fontSize: 20 }} />}
        >
          {t("Edit columns")}
        </Button>
      </div>
    </Dropdown>
  )
}

const type = "DraggableItem"

const DraggableItem = ({ index, label, moveRow, onClick, children }) => {
  const ref = useRef()
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {}
      if (dragIndex === index) {
        return {}
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ` drop-over-downward` : ` drop-over-upward`
      }
    },
    drop: (item) => {
      moveRow(item.index, index)
    }
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  preview(drop(ref))

  return (
    <Flex
      align="center"
      justify={"space-between"}
      key={label}
      ref={ref}
      className={cls({
        "custom-item-wrapper": index !== -1,
        [dropClassName]: index !== -1 && isOver
      })}
      onClick={onClick}
    >
      {children}
      {index !== -1 && (
        <span ref={drag} className="drag">
          <Icon type="grab" />
        </span>
      )}
    </Flex>
  )
}

export default ManageColumn
